import React, { useContext } from 'react';
import { GradeWatchContext } from '../context/GradeWatchContext';
import './WatchButton.css';

const BellIcon = ({ filled }) => (
  <svg
    width="16" height="16" viewBox="0 0 24 24"
    fill={filled ? 'currentColor' : 'none'}
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
  </svg>
);

/**
 * WatchButton — a bell toggle that adds/removes a ticker from Grade Watch
 * (grade-change alerts). Renders nothing for logged-out users. Stops click
 * propagation so it's safe inside clickable rows/cards.
 *
 * Reads GradeWatchContext directly rather than via useGradeWatch(), because this
 * button is meant to be dropped onto any surface and some of them render OUTSIDE
 * the provider: LoginGate returns PublicScreener / PrivacyPolicy / TermsOfService
 * / SmsAlerts directly instead of as `children`, so the provider tree never wraps
 * them. useGradeWatch() throws in that case, which would white-screen the public
 * screener. No provider means nobody is logged in anyway, so render nothing.
 */
export default function WatchButton({ symbol, size = 'sm', showLabel = false, className = '' }) {
  const ctx = useContext(GradeWatchContext);
  if (!ctx || !ctx.loggedIn || !symbol) return null;
  const { isWatched, toggle } = ctx;

  const watching = isWatched(symbol);
  return (
    <button
      type="button"
      className={`watch-btn watch-${size} ${watching ? 'watching' : ''} ${className}`.trim()}
      onClick={(e) => { e.stopPropagation(); toggle(symbol); }}
      aria-pressed={watching}
      aria-label={watching ? `Stop watching ${symbol}` : `Watch ${symbol} for grade-change alerts`}
      title={
        watching
          ? `Watching ${symbol} — you'll get an alert when its grade changes. Click to stop.`
          : `Watch ${symbol} — get an alert when its grade changes`
      }
    >
      <BellIcon filled={watching} />
      {showLabel && <span className="watch-label">{watching ? 'Watching' : 'Watch'}</span>}
    </button>
  );
}
